import { useCallback, useEffect, useRef, useState } from "react";

export type ScoutToolState =
  | "input_streaming"
  | "input_available"
  | "output_available"
  | "output_error";

export type ScoutTextPart = {
  id: string;
  type: "text";
  text: string;
};

export type ScoutToolPart = {
  id: string;
  type: "tool";
  toolName: string;
  toolDisplayName?: string;
  toolCallId: string;
  toolState: ScoutToolState;
  toolInput?: unknown;
  toolOutput?: unknown;
  toolError?: string;
  toolDurationMs?: number;
};

export type ScoutMessagePart = ScoutTextPart | ScoutToolPart;

export type ScoutChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  parts?: ScoutMessagePart[];
  isStreaming?: boolean;
};

function buildStubResponse(userPrompt: string): string {
  const preview = userPrompt.length > 120 ? `${userPrompt.slice(0, 120)}…` : userPrompt;
  return `Received the prompt "${preview}". Thinking and responding…`;
}

function buildStubToolParts(userPrompt: string): ScoutToolPart[] {
  const lower = userPrompt.toLowerCase();
  const parts: ScoutToolPart[] = [];

  if (lower.includes("single tool")) {
    parts.push({
      id: crypto.randomUUID(),
      type: "tool",
      toolName: "query_facilities",
      toolDisplayName: "Looking up facilities",
      toolCallId: `call_${crypto.randomUUID()}`,
      toolState: "output_available",
      toolInput: { tenantId: "demo-tenant", limit: 10 },
      toolOutput: { count: 62 },
      toolDurationMs: 142,
    });
    return parts;
  }

  if (lower.includes("facility") || lower.includes("facilities")) {
    parts.push({
      id: crypto.randomUUID(),
      type: "tool",
      toolName: "query_facilities",
      toolDisplayName: "Looking up facilities",
      toolCallId: `call_${crypto.randomUUID()}`,
      toolState: "output_available",
      toolInput: { tenantId: "demo-tenant", limit: 10 },
      toolOutput: { count: 62, durationMs: 142 },
      toolDurationMs: 142,
    });
  }

  if (lower.includes("chemical") || lower.includes("inventory")) {
    parts.push({
      id: crypto.randomUUID(),
      type: "tool",
      toolName: "search_chemicals",
      toolDisplayName: "Searching chemicals",
      toolCallId: `call_${crypto.randomUUID()}`,
      toolState: "output_available",
      toolInput: { query: "hazardous", threshold: 10000 },
      toolOutput: { rows: 23 },
      toolDurationMs: 310,
    });
  }

  if (parts.length === 0) {
    parts.push(
      {
        id: crypto.randomUUID(),
        type: "tool",
        toolName: "query_facilities",
        toolDisplayName: "Looking up facilities",
        toolCallId: `call_${crypto.randomUUID()}`,
        toolState: "output_available",
        toolInput: { status: "ACTIVE" },
        toolOutput: { count: 62 },
        toolDurationMs: 98,
      },
      {
        id: crypto.randomUUID(),
        type: "tool",
        toolName: "get_tier_ii_reports",
        toolDisplayName: "Fetching Tier II reports",
        toolCallId: `call_${crypto.randomUUID()}`,
        toolState: "output_available",
        toolInput: { year: 2025 },
        toolOutput: { rows: 12 },
        toolDurationMs: 221,
      },
      {
        id: crypto.randomUUID(),
        type: "tool",
        toolName: "summarize_findings",
        toolDisplayName: "Summarizing findings",
        toolCallId: `call_${crypto.randomUUID()}`,
        toolState: "output_available",
        toolInput: { topic: userPrompt.slice(0, 40) },
        toolOutput: { summary: "Pulled together context for response." },
        toolDurationMs: 57,
      }
    );
  }

  return parts;
}

function streamAssistantText(
  fullText: string,
  onChunk: (partial: string) => void,
  onDone: () => void
): () => void {
  let index = 0;
  let cancelled = false;
  const step = 2;
  const delayMs = 18;

  const tick = () => {
    if (cancelled) return;
    index = Math.min(fullText.length, index + step);
    onChunk(fullText.slice(0, index));
    if (index < fullText.length) {
      window.setTimeout(tick, delayMs);
    } else {
      onDone();
    }
  };

  tick();
  return () => {
    cancelled = true;
  };
}

export const useScoutChat = () => {
  const [messages, setMessages] = useState<ScoutChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const cancelStreamRef = useRef<(() => void) | null>(null);
  const streamingAssistantIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      cancelStreamRef.current?.();
    };
  }, []);

  const handleSubmit = useCallback(async (text: string) => {
    // If a previous assistant message is mid-stream, cancel it and mark it not streaming.
    cancelStreamRef.current?.();
    if (streamingAssistantIdRef.current) {
      const previousAssistantId = streamingAssistantIdRef.current;
      streamingAssistantIdRef.current = null;
      setMessages((prev) =>
        prev.map((m) => (m.id === previousAssistantId ? { ...m, isStreaming: false } : m))
      );
    }

    const userMessage: ScoutChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
    };
    const assistantId = crypto.randomUUID();
    streamingAssistantIdRef.current = assistantId;
    const stub = buildStubResponse(text);
    const finishedTools = buildStubToolParts(text);
    // Start all tools in the "input_available" state (args ready, executing)
    // so the UI can show "Checking…". They transition to "output_available"
    // one by one below.
    const pendingTools: ScoutToolPart[] = finishedTools.map((t) => ({
      ...t,
      toolState: "input_available",
      toolOutput: undefined,
      toolDurationMs: undefined,
    }));
    const textPartId = crypto.randomUUID();

    setMessages((prev) => [
      ...prev,
      userMessage,
      {
        id: assistantId,
        role: "assistant",
        content: "",
        isStreaming: true,
        parts: [...pendingTools, { id: textPartId, type: "text", text: "" }],
      },
    ]);
    setIsSending(true);

    const timers: number[] = [];
    let cancelled = false;

    // Stagger tool completions so the "Checking…" → "Checked" transition is
    // visible just for the mock UI
    const perToolDelayMs = 600;
    finishedTools.forEach((finished, i) => {
      const timer = window.setTimeout(
        () => {
          if (cancelled) return;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const updatedParts = m.parts?.map((p) =>
                p.type === "tool" && p.id === finished.id ? finished : p
              );
              return { ...m, parts: updatedParts };
            })
          );
        },
        perToolDelayMs * (i + 1)
      );
      timers.push(timer);
    });

    const textStartDelayMs = perToolDelayMs * (finishedTools.length + 1);
    const startTextTimer = window.setTimeout(() => {
      if (cancelled) return;
      const cancelStream = streamAssistantText(
        stub,
        (partial) => {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const updatedParts = m.parts?.map((p) =>
                p.id === textPartId && p.type === "text" ? { ...p, text: partial } : p
              );
              return { ...m, content: partial, parts: updatedParts, isStreaming: true };
            })
          );
        },
        () => {
          setIsSending(false);
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
          );
          if (streamingAssistantIdRef.current === assistantId) {
            streamingAssistantIdRef.current = null;
          }
          cancelStreamRef.current = null;
        }
      );
      cancelStreamRef.current = () => {
        cancelled = true;
        cancelStream();
        setIsSending(false);
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
        );
        if (streamingAssistantIdRef.current === assistantId) {
          streamingAssistantIdRef.current = null;
        }
        cancelStreamRef.current = null;
      };
    }, textStartDelayMs);
    timers.push(startTextTimer);

    cancelStreamRef.current = () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      setIsSending(false);
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
      );
      if (streamingAssistantIdRef.current === assistantId) {
        streamingAssistantIdRef.current = null;
      }
      cancelStreamRef.current = null;
    };
  }, []);

  return {
    messages,
    isSending,
    handleSubmit,
  };
};
